// src/pages/LoginAndAccountPage.jsx
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {supabase} from "../supabaseClient";
import "./css/LoginAndAccountPage.css";
import ToHomeBtn from "../components/Shared/ToHomepageBtn";
import {toast} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {ToastContainer} from "react-toastify";
import {useAuth} from "../contexts/AuthContext";

export default function LoginAndAccountPage() {
    const navigate = useNavigate();
    const {user, role, loading: authLoading} = useAuth();

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [profile, setProfile] = useState(null);
    const [userBoxes, setUserBoxes] = useState([]);

    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [newPhone, setNewPhone] = useState("");

    const [fetchTriggered, setFetchTriggered] = useState(false);

    // fetch profile & boxes once auth is ready
    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate("/login");
            return;
        }
        if (role === "admin") {
            navigate("/admin");
            return;
        }
        if (fetchTriggered) return;

        setFetchTriggered(true);
        (async () => {
            try {
                // guard: check session still valid
                const {data: sess} = await supabase.auth.getSession();
                if (!sess?.session) {
                    navigate("/login");
                    return;
                }

                // fetch profile
                const {data: userProfile, error: pErr} = await supabase.from("users").select("*").eq("id", user.id).single();
                if (pErr) throw pErr;
                setProfile(userProfile);

                // fetch boxes
                const {data: boxes, error: bErr} = await supabase.from("boxes").select("*").eq("user_id", user.id).order("id", {ascending: true});
                if (bErr) throw bErr;
                setUserBoxes(boxes);
            } catch (err) {
                console.error("Fetch error:", err);
                toast.error("Fel vid hämtning av data");
            } finally {
                setLoadingProfile(false);
            }
        })();
    }, [authLoading, user, role, fetchTriggered, navigate]);

    if (authLoading || loadingProfile) {
        return <p>Hämtar profil...</p>;
    }

    const handleLogout = async () => {
        // guard
        const {data: s} = await supabase.auth.getSession();
        if (!s?.session) {
            navigate("/login");
            return;
        }
        const {error} = await supabase.auth.signOut();
        if (error) {
            toast.error("Kunde inte logga ut");
            return;
        }
        navigate("/login");
    };

    const handleSaveEmail = async () => {
        // guard
        const {data: s} = await supabase.auth.getSession();
        if (!s?.session) {
            navigate("/login");
            return;
        }
        try {
            const {error} = await supabase.from("users").update({email: newEmail}).eq("id", profile.id);
            if (error) throw error;
            const {error: ae} = await supabase.auth.updateUser({email: newEmail});
            if (ae) throw ae;
            setProfile((p) => ({...p, email: newEmail}));
            setIsEditingEmail(false);
        } catch (err) {
            console.error("Save email failed:", err);
            toast.error("Fel vid uppdatering av e‑post");
        }
    };

    const handleSavePhone = async () => {
        // guard
        const {data: s} = await supabase.auth.getSession();
        if (!s?.session) {
            navigate("/login");
            return;
        }
        try {
            const {error} = await supabase.from("users").update({phone_number: newPhone}).eq("id", profile.id);
            if (error) throw error;
            setProfile((p) => ({...p, phone_number: newPhone}));
            setIsEditingPhone(false);
        } catch (err) {
            console.error("Save phone failed:", err);
            toast.error("Fel vid uppdatering av telefonnummer");
        }
    };

    return (
        <div className="login-account-page">
            <ToastContainer />
            <ToHomeBtn />
            <h1>Hantera konto</h1>

            <p>
                Email: {profile.email || "Saknas"}
                {!isEditingEmail && (
                    <button
                        className="edit-btn"
                        onClick={() => {
                            setNewEmail(profile.email || "");
                            setIsEditingEmail(true);
                        }}
                    >
                        Edit Email
                    </button>
                )}
            </p>
            {isEditingEmail && (
                <div className="edit-field-container">
                    <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                    <button className="edit-btn" onClick={handleSaveEmail}>
                        Save
                    </button>
                    <button className="edit-btn" onClick={() => setIsEditingEmail(false)}>
                        Cancel
                    </button>
                </div>
            )}

            <p>Namn: {profile.name || "Inget namn sparat"}</p>

            <p>
                Mobilnummer: {profile.phone_number || "Saknas"}
                {!isEditingPhone && (
                    <button
                        className="edit-btn"
                        onClick={() => {
                            setNewPhone(profile.phone_number || "");
                            setIsEditingPhone(true);
                        }}
                    >
                        Edit Phone
                    </button>
                )}
            </p>
            {isEditingPhone && (
                <div className="edit-field-container">
                    <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
                    <button className="edit-btn" onClick={handleSavePhone}>
                        Save
                    </button>
                    <button className="edit-btn" onClick={() => setIsEditingPhone(false)}>
                        Cancel
                    </button>
                </div>
            )}

            <h2>Mina Lådor</h2>
            {userBoxes.length > 0 ? (
                userBoxes.map((b) => (
                    <p key={b.id}>
                        Box ID: {b.id} | Expiration: {b.expiration_date}
                    </p>
                ))
            ) : (
                <p>Inga lådor hittade</p>
            )}

            <button className="logout-button" onClick={handleLogout}>
                Logga ut
            </button>

            <button 
                className="delete-account-button" 
                onClick={() => navigate("/delete-account")}
            >
                Ta bort konto
            </button>
        </div>
    );
}